export type ProductSuggestionInput = {
  name: string;
  description: string;
  category: string;
  tags: string[];
};

export type CategoryOption = {
  _id: string;
  name: string;
};

type MobileNetLike = {
  classify: (
    img: HTMLCanvasElement | HTMLImageElement,
    topk?: number,
  ) => Promise<Array<{ className: string; probability: number }>>;
};

let modelPromise: Promise<MobileNetLike> | null = null;

const THUMB = 192;

const loadModel = () => {
  if (!modelPromise) {
    modelPromise = (async () => {
      const tf = await import("@tensorflow/tfjs");
      tf.enableProdMode();
      await tf.ready();
      const mobilenet = await import("@tensorflow-models/mobilenet");
      const model = await mobilenet.load({ version: 1, alpha: 0.25 });
      const warm = document.createElement("canvas");
      warm.width = THUMB;
      warm.height = THUMB;
      await model.classify(warm, 1);
      return model;
    })();
  }
  return modelPromise;
};

export const preloadProductSuggestModel = () => {
  if (typeof window === "undefined") return;
  const run = () => {
    void loadModel();
  };
  if ("requestIdleCallback" in window) {
    window.requestIdleCallback(run, { timeout: 1200 });
  } else {
    window.setTimeout(run, 200);
  }
};

const toThumbCanvas = async (file: File) => {
  const bitmap = await createImageBitmap(file);
  const canvas = document.createElement("canvas");
  canvas.width = THUMB;
  canvas.height = THUMB;
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    bitmap.close();
    throw new Error("Could not read image");
  }
  const scale = Math.max(THUMB / bitmap.width, THUMB / bitmap.height);
  const width = bitmap.width * scale;
  const height = bitmap.height * scale;
  ctx.drawImage(bitmap, (THUMB - width) / 2, (THUMB - height) / 2, width, height);
  bitmap.close();
  return canvas;
};

const cleanLabel = (label: string) =>
  label.split(",")[0].replace(/_/g, " ").trim().toLowerCase();

const titleCase = (value: string) =>
  value.replace(/\b\w/g, (char) => char.toUpperCase());

const filled = (current: ProductSuggestionInput) => ({
  name: current.name.trim().length >= 2,
  description: current.description.trim().length >= 10,
  category: Boolean(current.category),
  tags: current.tags.length > 0,
});

const matchCategory = (labels: string[], categories: CategoryOption[]) => {
  if (categories.length === 0) return "";
  const haystack = labels.join(" ").toLowerCase();
  const scored = categories.map((category) => {
    const name = category.name.toLowerCase();
    const tokens = name.split(/\s+/).filter((token) => token.length > 2);
    let score = 0;
    if (haystack.includes(name)) score += 5;
    tokens.forEach((token) => {
      if (haystack.includes(token)) score += 2;
    });
    labels.forEach((label) => {
      if (name.includes(label) || label.includes(name)) score += 3;
    });
    return { category, score };
  });
  scored.sort((a, b) => b.score - a.score);
  return scored[0]?.score ? scored[0].category._id : "";
};

export async function suggestProductFromImages(
  files: File[],
  current: ProductSuggestionInput,
  categories: CategoryOption[],
): Promise<Partial<ProductSuggestionInput> | null> {
  const already = filled(current);
  if (already.name && already.description && already.category && already.tags) {
    return null;
  }

  const file = files[0];
  if (!file) return null;

  const [net, canvas] = await Promise.all([loadModel(), toThumbCanvas(file)]);
  const predictions = await net.classify(canvas, 3);

  const ranked = predictions
    .map((prediction) => cleanLabel(prediction.className))
    .filter((label) => label && label !== "product" && label.length > 1);

  if (ranked.length === 0) return null;

  const primary = titleCase(ranked[0]);
  const extras = ranked.slice(1, 3).map(titleCase);
  const suggestion: Partial<ProductSuggestionInput> = {};

  if (!already.name) suggestion.name = primary;
  if (!already.description) {
    const extraLine =
      extras.length > 0 ? ` Also similar to ${extras.join(", ").toLowerCase()}.` : "";
    suggestion.description = `A ${ranked[0]} ready for your store.${extraLine}`;
  }
  if (!already.category) {
    const categoryId = matchCategory(ranked, categories);
    if (categoryId) suggestion.category = categoryId;
  }
  if (!already.tags) {
    suggestion.tags = ranked.slice(0, 4).map((label) => titleCase(label));
  }

  return Object.keys(suggestion).length > 0 ? suggestion : null;
}
